/* DoorGlow cart API — adds/changes lines and notifies Horizon's cart drawer, cart page
 * and cart icon through the standard CartLinesUpdateEvent (same contract as
 * Horizon's product-form.js), so the drawer refreshes and auto-opens.
 *
 *   await DoorGlowCart.add([{ id, quantity, properties }], sourceElement)
 *   await DoorGlowCart.change({ key | line, quantity, properties }, sourceElement)
 *   await DoorGlowCart.swap(lineKey, { id, quantity, properties }, sourceElement)  // e.g. 2-light → 4-light
 * Each resolves with the ajax cart, or throws Error(message) on failure.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const routes = () => window.Theme?.routes || {};
const sectionIds = () => [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
async function getCart(tries = 2) {
  for (let i = 1; i <= tries; i++) {
    try {
      const res = await fetch(`${routes().cart_url || '/cart'}.js`, { credentials: 'same-origin' });
      return await res.json();
    } catch {
      await new Promise((r) => setTimeout(r, 200 * i));
    }
  }
  // Fallback: an empty update is a no-op that returns the full cart.
  const res = await fetch(`${routes().cart_update_url || '/cart/update'}.js`, {
    method: 'POST', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: '{}',
  });
  return res.json();
}

/** context: 'product' when adding from a product page, 'cart' for changes made in the cart/drawer. */
const contextFor = (source) => (source?.closest?.('cart-drawer-component, cart-items-component, #cart-drawer, .cart-drawer') ? 'cart' : 'product');

async function run(action, lines, source, request) {
  const deferred = CartLinesUpdateEvent.createPromise();
  (source || document).dispatchEvent(new CartLinesUpdateEvent({
    action,
    context: contextFor(source),
    lines: lines.map((l) => ({ merchandiseId: String(l.id ?? l.key), quantity: Number(l.quantity ?? 1) })),
    promise: deferred.promise,
  }));
  try {
    const response = await request();
    // /cart/change.js already returns the whole cart; /cart/add.js only returns the added lines.
    const cart = response && response.token && Array.isArray(response.items) ? response : await getCart();
    const error = response.status && response.status !== 200 ? (response.description || response.message || 'Something went wrong') : null;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'doorglow', itemCount: lines.reduce((n, l) => n + Number(l.quantity ?? 1), 0), sections: response.sections, didError: !!error },
    });
    if (error) throw new Error(error);
    return cart;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

function post(url, body) {
  const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };
  if (!(body instanceof FormData)) headers['Content-Type'] = 'application/json';
  return fetch(url, { method: 'POST', credentials: 'same-origin', headers, body: body instanceof FormData ? body : JSON.stringify(body) }).then((r) => r.json());
}

const DoorGlowCart = {
  getCart,
  add(lines, source) {
    const ids = sectionIds();
    const body = { items: lines.map((l) => ({ id: Number(l.id), quantity: Number(l.quantity ?? 1), properties: l.properties || {} })) };
    if (ids.length) body.sections = ids.join(',');
    return run('add', lines, source, () => post(routes().cart_add_url || '/cart/add.js', body));
  },
  change(line, source) {
    const ids = sectionIds();
    const body = { quantity: Number(line.quantity), ...(line.key ? { id: line.key } : { line: line.line }) };
    if (line.properties) body.properties = line.properties;
    if (ids.length) body.sections = ids.join(',');
    // Standard event actions are 'add' | 'update' | 'remove'.
    return run(Number(line.quantity) === 0 ? 'remove' : 'update', [line], source, () => post(`${routes().cart_change_url || '/cart/change'}.js`, body));
  },
  /** Replace one cart line with another variant, keeping its properties (upsells).
   *  `next.properties` should be passed by the caller (the drawer button carries them),
   *  so no cart read is needed. */
  async swap(lineKey, next, source) {
    // Add the new line first (silently) so the cart is never empty mid-swap, then
    // remove the old line; that change's event refreshes the drawer once.
    const added = await post(routes().cart_add_url || '/cart/add.js', {
      items: [{ id: Number(next.id), quantity: Number(next.quantity ?? 1), properties: next.properties || {} }],
    });
    if (added.status && added.status !== 200) throw new Error(added.description || added.message || 'Could not upgrade');
    return DoorGlowCart.change({ key: lineKey, quantity: 0 }, source);
  },
};

/* Drawer buttons (markup re-renders on every cart change, so listen on document) */
document.addEventListener('click', async (e) => {
  const swap = e.target.closest('[data-dg-upsell-swap]');
  const acc = e.target.closest('[data-dg-accessory-add]');
  const btn = swap || acc;
  if (!btn) return;
  e.preventDefault();
  btn.classList.add('is-loading');
  btn.setAttribute('aria-busy', 'true');
  try {
    if (swap) {
      let properties = {};
      try {
        const raw = JSON.parse(swap.dataset.properties || '{}');
        properties = Array.isArray(raw) ? Object.fromEntries(raw) : raw; // Liquid emits [[key, value], …]
      } catch { /* none */ }
      await DoorGlowCart.swap(swap.dataset.lineKey, { id: swap.dataset.variantId, quantity: Number(swap.dataset.quantity) || 1, properties }, swap);
    }
    else await DoorGlowCart.add([{ id: acc.dataset.variantId, quantity: 1 }], acc);
  } catch (err) {
    console.warn('[doorglow-cart]', err);
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
  }
});

window.DoorGlowCart = DoorGlowCart;
document.dispatchEvent(new CustomEvent('doorglow:cart-ready'));
export default DoorGlowCart;
